import { Injectable } from '@angular/core';
import { EupRoutesService } from '@core/eupRoutes.service';
import { TimberService } from '@logging/timber.service';
import { IteroPlatformLogin, Platform } from '@nodeModules/@itero/itero-platform-login';
import { Consts } from '@shared/consts';
import { BehaviorSubject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { GlobalSettingsService } from '@core/globalSettings.service';
import { AppConfigService } from '../appConfig/appConfigService';
import { IteroPlatformLoginInput, LoginResponse } from './models/platform-login.models';
import { jwtDecode } from 'jwt-decode';
import { LocalStorageService } from '@core/localStorage.service';
import { SessionInfo } from '../authentication/auth.service';

@Injectable({
	providedIn: 'root',
})
export class PlatformLoginService {
	private iteroPlatformLogin: IteroPlatformLogin;
	private readonly initialized$ = new BehaviorSubject<boolean>(false);

	constructor(
		private readonly eupRoutesService: EupRoutesService,
		private readonly timberService: TimberService,
		private readonly globalSettingsService: GlobalSettingsService,
		private readonly appConfigService: AppConfigService,
		private readonly localStorageService: LocalStorageService
	) {}

	initialize(): void {
		const serviceInput = new IteroPlatformLoginInput();

		serviceInput.clientDiscoveryEndpoint = this.eupRoutesService.clientDiscoveryUrl;
		serviceInput.loggerService = this.timberService;
		serviceInput.smsEndpoint = this.getSmsRootEndpoint();
		serviceInput.loginBffEndpoint = this.appConfigService.appSettings.routingData.loginBffUrl;

		this.iteroPlatformLogin = new IteroPlatformLogin(serviceInput);
		// eslint-disable-next-line no-console
		console.log(`[PlatformLoginService] Itero platform login package initialized with login service ${serviceInput.loginBffEndpoint}`);
		this.initialized$.next(true);
	}

	login(): void {
		this.iteroPlatformLogin.login();
	}

	iteroPlatformLoginUsed(): boolean {
		const username = this.getAuthenticatedUsername();

		return username ? this.iteroPlatformLogin.isNewLoginFlow(username, Consts.AppId) : false;
	}

	setLegacyLogin(): void {
		const username = this.getAuthenticatedUsername();

		if (username) {
			this.iteroPlatformLogin.setLegacyLoginFlow(username, Consts.AppId);
		}
	}

	getLoginInfo(loginHint: string, state: string, sessionId: string, loginCallback: (loginResponse: LoginResponse) => void): void {
		this.initialized$
			.pipe(
				filter(ready => !!ready),
				take(1)
			)
			.subscribe(() => {
				this.iteroPlatformLogin.getLoginInfo(Consts.AppId, loginHint, sessionId, state, loginCallback);
			});
	}

	async getUserProvider(accessToken: string): Promise<string | null> {
		if (!accessToken) {
			this.timberService.warn('Missing accessToken in getUserProvider', { module: 'PlatformLoginService' });

			return null;
		}

		const res = await this.iteroPlatformLogin.getUserProvider(accessToken);

		return res ? res.provider : null;
	}

	async canUserAccessLabPlatform(accessToken: string): Promise<boolean> {
		if (!accessToken) {
			this.timberService.warn('Missing accessToken in canUserAccessLabPlatform', { module: 'PlatformLoginService' });

			return false;
		}

		return this.iteroPlatformLogin.canUserAccessPlatform(Platform.Lab, accessToken);
	}

	updateToken(username: string, sessionId: string, accessToken: string): void {
		if (this.iteroPlatformLoginUsed()) {
			this.iteroPlatformLogin.updateToken(username, sessionId, accessToken);
			this.timberService.info(`Token updated in itero platform login package for ${username} and session id ${sessionId}`, {
				module: 'PlatformLoginService',
			});
		}
	}

	async logout(idToken: string, sessionId: string, username: string): Promise<void> {
		this.timberService.info(`Platform login package logout called for session ${sessionId}`, { module: 'PlatformLoginService' });
		await this.iteroPlatformLogin.logout(idToken, sessionId, username, Consts.AppId);
	}

	relogin(returnUri: string, loginHint: string): void {
		this.timberService.info(`Platform login package relogin called for ${loginHint}`, { module: 'PlatformLoginService' });
		this.iteroPlatformLogin.relogin(Consts.AppId, returnUri, loginHint);
	}

	// Azure tokens carry the identity in preferred_username; others use sub/email.
	getEmailFromAccessToken(accessToken: string | undefined): string {
		if (!accessToken) {
			return '';
		}

		try {
			const decoded = jwtDecode<Record<string, string | undefined>>(accessToken);
			const username = decoded?.preferred_username;
			const sub = decoded?.sub;
			const email = sub && !sub.startsWith('auth0|') ? sub : decoded?.email || '';

			return username || (email.trim() ? email : '');
		} catch (error) {
			this.timberService.error('Error decoding access token for email extraction', {
				module: 'PlatformLoginService',
				error: error instanceof Error ? error : new Error(String(error)),
			});

			return '';
		}
	}

	// Authenticated user from the session token (correct during logon-as); falls back to globalSettings.
	private getAuthenticatedUsername(): string {
		const sessionInfo = this.localStorageService.getItemOrEmpty<SessionInfo>(Consts.Storage.SessionInfo);
		const tokenEmail = this.getEmailFromAccessToken(sessionInfo?.accessToken);

		return tokenEmail || (this.globalSettingsService.get()?.loginEmail ?? '');
	}

	private getSmsRootEndpoint(): string {
		const authApiUrl = this.eupRoutesService.iTeroWebAuthApiUrl;

		if (!authApiUrl) {
			return authApiUrl;
		}

		try {
			return `${new URL(authApiUrl).origin}/`;
		} catch {
			return authApiUrl;
		}
	}
}

export { IteroPlatformLoginInput, LoginResponse } from './models/platform-login.models';
